import { useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { clsx } from "clsx";

/**
 * The one fold toggle (UI cleanup U2): a triangle that turns, the label, and on the right an optional count. Used
 * by Settings sections and their *Details*, the task panel's groups, and every smaller fold (`Fold`); before, each
 * had its own markup, some the browser's `<details>`, which looks and works differently.
 */
export function FoldToggle({
  open,
  onToggle,
  children,
  trailing,
  heading = false,
  testId,
  className,
}: {
  open: boolean;
  onToggle(): void;
  children: ReactNode;
  /** shown at the right end, e.g. how many values are set */
  trailing?: ReactNode;
  /** a section heading (small capitals) rather than a line of text */
  heading?: boolean;
  testId?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      data-testid={testId}
      className={clsx(
        "flex items-center gap-1.5 text-left",
        heading
          ? "w-full text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
          : "w-fit text-[12.5px] text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100",
        className,
      )}
    >
      <ChevronRight
        size={heading ? 13 : 12}
        aria-hidden
        className={clsx("shrink-0 transition-transform motion-reduce:transition-none", open && "rotate-90")}
      />
      {children}
      {trailing !== undefined && trailing !== null && trailing !== false && (
        <span className="ml-auto font-mono text-[11px] font-normal normal-case tracking-normal text-neutral-400">
          {trailing}
        </span>
      )}
    </button>
  );
}

/** A small fold with its own open state: the toggle, then the content while open. */
export function Fold({
  label,
  children,
  heading = false,
  defaultOpen = false,
  testId,
  toggleTestId,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  heading?: boolean;
  defaultOpen?: boolean;
  testId?: string;
  toggleTestId?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={clsx("flex flex-col gap-1.5", className)} data-testid={testId} data-open={open}>
      <FoldToggle open={open} onToggle={() => setOpen((v) => !v)} heading={heading} testId={toggleTestId}>
        {label}
      </FoldToggle>
      {open && children}
    </div>
  );
}
